/**
 * Engine order channel — single-slot short command with expiry.
 * Domain owns order kinds, their meaning, and pre-emption policy (which AI
 * tiers an order may suppress); this kit only stores/expires the record.
 * Deliberately a single slot (latest wins) — no queue at proto scale.
 */

export type Order<K extends string = string> = {
  kind: K;
  issuedAt: number;
  /** Absolute sim time the order expires ("short directive" semantics). */
  until: number;
  /** 1 = bias (advisory), 2 = forced (suppresses survival tiers). */
  priority: 1 | 2;
  targetId?: number | null;
  x?: number;
  y?: number;
};

export type OrderSlot<K extends string = string> = {
  current: Order<K> | null;
};

export function createOrderSlot<K extends string = string>(): OrderSlot<K> {
  return { current: null };
}

/** Latest order wins (overwrites any current one). */
export function issueOrder<K extends string>(
  slot: OrderSlot<K>,
  order: Order<K>,
): void {
  slot.current = order;
}

export function clearOrder(slot: OrderSlot<string>): void {
  slot.current = null;
}

/** Active order at `time`; expired orders are cleared lazily. */
export function activeOrder<K extends string>(
  slot: OrderSlot<K>,
  time: number,
): Order<K> | null {
  const o = slot.current;
  if (!o) return null;
  if (time >= o.until) {
    slot.current = null;
    return null;
  }
  return o;
}
